/**
 * @fileoverview Shared modal for adding code-rendered system modules to a template canvas.
 * @module template-builder
 *
 * Responsibilities:
 * - Let administrators choose Contacts or Additional Violations module forms.
 * - Configure module-specific options before the host app creates the module form.
 * - Load selectable violation code sets through an injected local-first host service.
 *
 * Dependencies:
 * - lucide-react icons
 * - shared template and violation code set contracts
 * - host-provided ViolationCodeSetService implementations
 *
 * Architecture:
 * - Shared UI-only package component; it never writes to Dexie or calls API routes directly.
 * - Host apps own local-first reads, writes, and sync behavior through injected services.
 *
 * Notes:
 * - The noop service is only a safe empty default, not mock production data.
 */
import { useState, useEffect } from 'react';
import { useContactRoles } from '../../hooks/useContactRoles';
import { X, Users, AlertTriangle, ChevronRight, Plus, Trash2 } from 'lucide-react';
import type {
  ModuleType, ContactsModuleConfig, ViolationsModuleConfig,
  ContactRoleConfig,
} from '../../types/templateTypes';
import type { ViolationCodeSet } from '../../types/violationCodeSet';
import { noopViolationCodeSetService, type ViolationCodeSetService } from '../../services/violationCodeSetService';

// ---------------------------------------------------------------------------
// Module catalogue entry
// ---------------------------------------------------------------------------

const MODULE_CATALOGUE: {
  type: ModuleType;
  icon: React.ReactNode;
  label: string;
  description: string;
  color: string;
}[] = [
  {
    type: 'contacts',
    icon: <Users size={20} />,
    label: 'Contacts',
    description: 'Inspector-managed contacts list. Define required roles (Owner, Responsible Party, Billing Contact, etc.) with minimum counts.',
    color: 'blue',
  },
  {
    type: 'violations',
    icon: <AlertTriangle size={20} />,
    label: 'Additional Violations',
    description: 'Ad-hoc violation logging against selected code sets. Inspector searches by keyword, picks a code, and logs it to the case.',
    color: 'orange',
  },
];

const COLOR_MAP: Record<string, { bg: string; icon: string; ring: string; badge: string }> = {
  blue:   { bg: 'bg-blue-50',   icon: 'text-blue-600',   ring: 'ring-blue-300',   badge: 'bg-blue-100 text-blue-700' },
  orange: { bg: 'bg-orange-50', icon: 'text-orange-600', ring: 'ring-orange-300', badge: 'bg-orange-100 text-orange-700' },
};

// ---------------------------------------------------------------------------
// Default configs
// ---------------------------------------------------------------------------

const DEFAULT_CONTACTS_CONFIG: ContactsModuleConfig = {
  moduleType: 'contacts',
  roles: [
    { role: 'Owner',              minRequired: 1, allowMultiple: false },
    { role: 'Responsible Party',  minRequired: 1, allowMultiple: false },
    { role: 'Billing Contact',    minRequired: 0, allowMultiple: false },
    { role: 'On-site Contact',    minRequired: 0, allowMultiple: true  },
  ],
  allowAddNew: true,
  allowSelectExisting: true,
};

const DEFAULT_VIOLATIONS_CONFIG: ViolationsModuleConfig = {
  moduleType: 'violations',
  codeSetIds: [],
  allowAiSearch: true,
  showNotes: true,
  requireNotes: false,
  showRemedy: false,
  showSeverity: false,
  showPhotos: true,
  requirePhotos: false,
  allowCorrectiveAction: true,
};

/**
 * @component ModuleTypePicker
 *
 * Purpose:
 * - Presents the tenant-proven module type selection behavior in the shared package.
 *
 * Props:
 * - onSelect: Receives the selected module type.
 * - disabledTypes: Module types already present on the canvas.
 *
 * Behavior:
 * - Disables already-added module types and advances selectable module types to configuration.
 *
 * Data Source:
 * - local
 *
 * Constraints:
 * - UI-only; must not call APIs, Dexie, or host services.
 *
 * Performance:
 * - Renders a fixed small catalogue with no async work.
 */
function ModuleTypePicker({ onSelect, disabledTypes = new Set() }: { onSelect: (t: ModuleType) => void; disabledTypes?: Set<string> }) {
  return (
    <div className="p-6 space-y-3">
      <p className="text-xs text-gray-500 mb-4">
        System modules are code-rendered forms. They appear on the canvas as locked cards
        and write directly to the local database during an inspection.
      </p>
      {MODULE_CATALOGUE.map((m) => {
        const c = COLOR_MAP[m.color];
        const isDisabled = disabledTypes.has(m.type);
        return (
          <button
            key={m.type}
            onClick={() => !isDisabled && onSelect(m.type)}
            disabled={isDisabled}
            className={`w-full flex items-center gap-4 p-4 rounded-xl border text-left transition-all group ${
              isDisabled
                ? 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed'
                : 'border-gray-200 hover:border-gray-300 hover:shadow-xs'
            }`}
          >
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${c.bg} ${c.icon}`}>
              {m.icon}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-gray-800">{m.label}</div>
              <div className="text-xs text-gray-500 mt-0.5">{m.description}</div>
            </div>
            {isDisabled
              ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-400 font-medium shrink-0">Already added</span>
              : <ChevronRight size={16} className="text-gray-300 group-hover:text-gray-500 shrink-0 transition-colors" />}
          </button>
        );
      })}
    </div>
  );
}

/**
 * @component ContactsConfigurator
 *
 * Purpose:
 * - Configures the Contacts system module before it is added to the canvas.
 *
 * Props:
 * - config: Current contacts module configuration.
 * - onChange: Emits configuration changes to the parent modal.
 *
 * Behavior:
 * - Preserves the tenant-proven contact role editor and contact entry options.
 *
 * Data Source:
 * - local
 *
 * Constraints:
 * - UI-only; host canvas creates and persists the module form after confirmation.
 *
 * Performance:
 * - Updates only local modal state through the provided callback.
 */
export function ContactsConfigurator({
  config,
  onChange,
}: {
  config: ContactsModuleConfig;
  onChange: (c: ContactsModuleConfig) => void;
}) {
  const availableRoles = useContactRoles();
  const updateRole = (idx: number, patch: Partial<ContactRoleConfig>) => {
    const roles = config.roles.map((r, i) => i === idx ? { ...r, ...patch } : r);
    onChange({ ...config, roles });
  };
  const addRole = () => {
    onChange({ ...config, roles: [...config.roles, { role: '', minRequired: 0, allowMultiple: false }] });
  };
  const removeRole = (idx: number) => {
    onChange({ ...config, roles: config.roles.filter((_, i) => i !== idx) });
  };

  return (
    <div className="p-6 space-y-5">
      <div>
        <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-2">Contact Roles</label>
        <div className="space-y-2">
          {config.roles.map((role, idx) => (
            <div key={idx} className="flex items-center gap-2 p-2 rounded-lg bg-gray-50 border border-gray-100">
              <select
                value={role.role}
                onChange={(e) => updateRole(idx, { role: e.target.value })}
                className="flex-1 px-2 py-1 text-xs border border-gray-200 rounded-md focus:outline-hidden focus:ring-1 focus:ring-blue-300 bg-white min-w-0"
              >
                <option value="">-- Select role --</option>
                {availableRoles.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
              <select
                value={role.minRequired}
                onChange={(e) => updateRole(idx, { minRequired: Number(e.target.value) })}
                className="px-2 py-1 text-xs border border-gray-200 rounded-md focus:outline-hidden bg-white"
                title="Minimum required"
              >
                <option value={0}>Optional</option>
                <option value={1}>Min 1</option>
                <option value={2}>Min 2</option>
              </select>
              <label className="flex items-center gap-1 text-[10px] text-gray-500 whitespace-nowrap cursor-pointer">
                <input
                  type="checkbox"
                  checked={role.allowMultiple}
                  onChange={(e) => updateRole(idx, { allowMultiple: e.target.checked })}
                  className="rounded-sm"
                />
                Multi
              </label>
              <button
                title="Remove role"
                onClick={() => removeRole(idx)}
                className="p-1 text-gray-300 hover:text-red-400 transition-colors"
              >
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>
        <button
          onClick={addRole}
          className="mt-2 flex items-center gap-1.5 px-3 py-1.5 text-xs text-blue-600 border border-dashed border-blue-300 rounded-lg hover:bg-blue-50 transition-colors"
        >
          <Plus size={12} /> Add role
        </button>
      </div>

      <div className="space-y-2">
        <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Contact Entry</label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={config.allowAddNew}
            onChange={(e) => onChange({ ...config, allowAddNew: e.target.checked })}
            className="rounded-sm"
          />
          <span className="text-xs text-gray-700">Inspector can add, edit, and delete contacts</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={config.allowSelectExisting}
            onChange={(e) => onChange({ ...config, allowSelectExisting: e.target.checked })}
            className="rounded-sm"
          />
          <span className="text-xs text-gray-700">Inspector can select from existing contacts</span>
        </label>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 2b — Violations config
// ---------------------------------------------------------------------------

/**
 * @component ViolationsConfigurator
 *
 * Purpose:
 * - Configures the Additional Violations system module before it is added to the canvas.
 *
 * Props:
 * - config: Current violations module configuration.
 * - onChange: Emits configuration changes to the parent modal.
 * - codeSetService: Host-provided local-first code set service.
 * - allowDeferViolations: when true, code set selection is optional (system-web).
 *
 * Behavior:
 * - Loads active code sets from the injected service and requires at least one selection.
 * - When allowDeferViolations is true and no code sets exist, shows a deferred-config note.
 *
 * Data Source:
 * - local
 *
 * Constraints:
 * - Must not call APIs directly; host services own local-first access and sync.
 *
 * Performance:
 * - Loads code sets once per service instance.
 */
export function ViolationsConfigurator({
  config,
  onChange,
  codeSetService = noopViolationCodeSetService,
  allowDeferViolations = false,
}: {
  config: ViolationsModuleConfig;
  onChange: (c: ViolationsModuleConfig) => void;
  codeSetService?: ViolationCodeSetService;
  allowDeferViolations?: boolean;
}) {
  const [codeSets, setCodeSets] = useState<ViolationCodeSet[]>([]);

  useEffect(() => {
    codeSetService.listCodeSets().then((cs) => setCodeSets(cs.filter((c) => c.isActive)));
  }, [codeSetService]);

  const toggleCodeSet = (id: string) => {
    const ids = config.codeSetIds.includes(id)
      ? config.codeSetIds.filter((x) => x !== id)
      : [...config.codeSetIds, id];
    onChange({ ...config, codeSetIds: ids });
  };

  return (
    <div className="p-6 space-y-5">
      <div>
        <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-2">
          Searchable Code Sets <span className="text-red-400">*</span>
        </label>
        {codeSets.length === 0 ? (
          <p className="text-xs text-gray-400 italic">
            {allowDeferViolations
              ? 'No code sets available at the system level. Code sets will be selected per-department when this template is adopted.'
              : 'No active code sets found. Activate code sets in the Violation Codes section first.'}
          </p>
        ) : (
          <div className="space-y-1.5">
            {codeSets.map((cs) => (
              <label key={cs._id} className="flex items-center gap-2 p-2 rounded-lg border border-gray-100 bg-gray-50 cursor-pointer hover:bg-orange-50 hover:border-orange-200 transition-colors">
                <input
                  type="checkbox"
                  checked={config.codeSetIds.includes(cs._id)}
                  onChange={() => toggleCodeSet(cs._id)}
                  className="rounded-sm"
                />
                <div className="flex-1 min-w-0">
                  <span className="text-xs font-medium text-gray-800">{cs.name}</span>
                  {cs.description && <span className="text-[10px] text-gray-400 ml-1.5">{cs.description}</span>}
                </div>
                <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-sm bg-orange-100 text-orange-700 shrink-0">
                  {cs.source?.toUpperCase() ?? '—'}
                </span>
              </label>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-2">
        <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Options</label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={config.allowAiSearch} onChange={(e) => onChange({ ...config, allowAiSearch: e.target.checked })} className="rounded-sm" />
          <div>
            <span className="text-xs text-gray-700">AI-assisted search</span>
            <span className="ml-1.5 text-[9px] px-1 py-0.5 rounded-sm bg-purple-100 text-purple-600 font-medium">Tier 2+</span>
          </div>
        </label>
      </div>

      <div className="space-y-1">
        <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-2">Notes per violation</label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={config.showNotes}
            onChange={(e) => onChange({ ...config, showNotes: e.target.checked, requireNotes: e.target.checked ? config.requireNotes : false })}
            className="rounded-sm"
          />
          <span className="text-xs text-gray-700">Show notes field</span>
        </label>
        <label className={`flex items-center gap-2 cursor-pointer ${!config.showNotes ? 'opacity-40 pointer-events-none' : ''}`}>
          <input
            type="checkbox"
            checked={config.requireNotes}
            onChange={(e) => onChange({ ...config, requireNotes: e.target.checked })}
            className="rounded-sm"
            disabled={!config.showNotes}
          />
          <span className="text-xs text-gray-700">Require note before submitting</span>
        </label>
      </div>

      <div className="space-y-1">
        <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-2">Photos per violation</label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={config.showPhotos}
            onChange={(e) => onChange({ ...config, showPhotos: e.target.checked, requirePhotos: e.target.checked ? config.requirePhotos : false })}
            className="rounded-sm"
          />
          <span className="text-xs text-gray-700">Show photo attachment</span>
        </label>
        <label className={`flex items-center gap-2 cursor-pointer ${!config.showPhotos ? 'opacity-40 pointer-events-none' : ''}`}>
          <input
            type="checkbox"
            checked={config.requirePhotos}
            onChange={(e) => onChange({ ...config, requirePhotos: e.target.checked })}
            className="rounded-sm"
            disabled={!config.showPhotos}
          />
          <span className="text-xs text-gray-700">Require photo before submitting</span>
        </label>
      </div>
    </div>
  );
}

/**
 * @contract ModulePickerModalProps
 *
 * Purpose:
 * - Defines the shared module picker boundary consumed by tenant-web and system-web canvases.
 *
 * Fields:
 * - onConfirm: Callback receiving selected module type, module configuration, and canvas name.
 * - onClose: Callback for closing the modal without adding a module.
 * - disabledTypes: Existing module types that cannot be added again.
 * - violationCodeSetService: Optional host-provided local-first code set service.
 * - allowDeferViolations: when true (system-web), Violations module can be added without
 *   selecting code sets — department code sets are not yet known at the system level.
 *
 * Rules:
 * - Hosts must provide persistence through onConfirm; this modal does not write records.
 * - violationCodeSetService must read through host local-first services, not direct API calls.
 *
 * Sync:
 * - Confirmation delegates to host services that write through local-first form/template flows.
 */
export interface ModulePickerModalProps {
  onConfirm: (moduleType: ModuleType, config: ContactsModuleConfig | ViolationsModuleConfig, name: string) => void;
  onClose: () => void;
  disabledTypes?: Set<string>;
  violationCodeSetService?: ViolationCodeSetService;
  /** When true, the Violations module can be added without selecting code sets (system-web only). */
  allowDeferViolations?: boolean;
}

/**
 * @component ModulePickerModal
 *
 * Purpose:
 * - Presents shared Contacts and Additional Violations module creation UI for both host apps.
 *
 * Props:
 * - onConfirm: Receives the selected module type, configuration, and display name.
 * - onClose: Closes the modal.
 * - disabledTypes: Module types already present on the canvas.
 * - violationCodeSetService: Host-provided local-first code set service.
 *
 * Behavior:
 * - Walks the user through picking and configuring one code-rendered system module.
 *
 * Data Source:
 * - hybrid
 *
 * Constraints:
 * - Shared component must remain host-agnostic and cannot bypass Dexie services.
 *
 * Performance:
 * - Keeps modal state local and delegates persistence to the host callback.
 */
export function ModulePickerModal({ onConfirm, onClose, disabledTypes = new Set(), violationCodeSetService = noopViolationCodeSetService, allowDeferViolations = false }: ModulePickerModalProps) {
  const [step, setStep] = useState<'pick' | 'configure'>('pick');
  const [moduleType, setModuleType] = useState<ModuleType | null>(null);
  const [contactsConfig, setContactsConfig] = useState<ContactsModuleConfig>(DEFAULT_CONTACTS_CONFIG);
  const [violationsConfig, setViolationsConfig] = useState<ViolationsModuleConfig>(DEFAULT_VIOLATIONS_CONFIG);
  const [name, setName] = useState('');

  const handleSelectType = (t: ModuleType) => {
    setModuleType(t);
    setName(t === 'contacts' ? 'Contacts' : 'Additional Violations');
    setStep('configure');
  };

  const handleConfirm = () => {
    if (!moduleType) return;
    const config = moduleType === 'contacts' ? contactsConfig : violationsConfig;
    // Violations require at least one code set unless the host opts into deferred configuration.
    if (moduleType === 'violations' && violationsConfig.codeSetIds.length === 0 && !allowDeferViolations) return;
    onConfirm(moduleType, config, name.trim() || (moduleType === 'contacts' ? 'Contacts' : 'Additional Violations'));
  };

  const catalogue = MODULE_CATALOGUE.find((m) => m.type === moduleType);
  const c = catalogue ? COLOR_MAP[catalogue.color] : null;

  const canConfirm = moduleType === 'contacts'
    ? contactsConfig.roles.some((r) => r.role.trim())
    : violationsConfig.codeSetIds.length > 0 || allowDeferViolations;

  return (
    <div className="fixed inset-0 z-200 flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30" />
      <div
        className="relative bg-white rounded-2xl shadow-2xl border border-gray-200 w-[520px] max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="shrink-0 flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-3">
            {step === 'configure' && catalogue && c && (
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${c.bg} ${c.icon}`}>
                {catalogue.icon}
              </div>
            )}
            <div>
              <h2 className="text-base font-semibold text-gray-900">
                {step === 'pick' ? 'Add System Module' : `Configure ${catalogue?.label}`}
              </h2>
              <p className="text-xs text-gray-400">
                {step === 'pick' ? 'Choose a module type' : 'Set options for this module'}
              </p>
            </div>
          </div>
          <button title="Close" onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors">
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">
          {step === 'pick' && <ModuleTypePicker onSelect={handleSelectType} disabledTypes={disabledTypes} />}
          {step === 'configure' && moduleType === 'contacts' && (
            <ContactsConfigurator config={contactsConfig} onChange={setContactsConfig} />
          )}
          {step === 'configure' && moduleType === 'violations' && (
            <ViolationsConfigurator
              config={violationsConfig}
              onChange={setViolationsConfig}
              codeSetService={violationCodeSetService}
              allowDeferViolations={allowDeferViolations}
            />
          )}
        </div>

        {step === 'configure' && (
          <div className="shrink-0 border-t px-6 py-4 flex flex-wrap items-end gap-3">
            <div className="min-w-[220px] flex-1">
              <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Module name on canvas</label>
              <input
                type="text"
                value={name}
                title="Module name on canvas"
                placeholder="e.g. Contacts, Violations…"
                onChange={(e) => setName(e.target.value)}
                className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-hidden focus:ring-1 focus:ring-blue-300"
              />
            </div>
            <div className="ml-auto flex shrink-0 items-center gap-2 self-end">
              <button
                onClick={() => setStep('pick')}
                className="px-3 py-1.5 text-xs text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Back
              </button>
              <button
                onClick={handleConfirm}
                disabled={!canConfirm}
                className="whitespace-nowrap px-4 py-1.5 text-xs font-medium text-white rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-colors bg-(--brand-primary)"
              >
                Add to Canvas
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ModulePickerModal;
